import {
  Routes,
  Route,
  RoutesProps,
  Navigate,
} from "react-router-dom";

import React, { Suspense } from "react";
import ProtectedRoute from "./ProtectedRoute";
import Main from "../components/Main";
import ForgotPassword from "../components/Login/ForgotPassword";
import VerifyOTP from "../components/Login/VerifyOTP";
import CreatePassword from "../components/Login/CreatePassword";
import AddCMS from "../pages/AddCMS/AddCMS";
import PublicRoute from "./PublicRoute";

const Navigation = () => {

  interface ProtectedRouteProps extends RoutesProps {
    authenticationPath: string;
  }
  const defaultProtectedRouteProps: Omit<ProtectedRouteProps, "outlet"> = {
    authenticationPath: "/login",
  };

  const Login = React.lazy(
    () => import("../components/Login/Login")
  );
  const Dashboard = React.lazy(
    () => import("../pages/Dashboard/Dashboard")
  );
  const BusinessRevenue = React.lazy(
    () => import("../pages/BusinessRevenue/BusinessRevenue")
  );
  const MyProfile = React.lazy(
    () => import("../pages/MyProfile/MyProfile")
  );
  const ManageRoles = React.lazy(
    () => import("../pages/ManageRoles/ManageRoles")
  );
  const ManageUsers = React.lazy(
    () => import("../pages/ManageUsers/ManageUsers")
  );
  const ManageAddUsers = React.lazy(
    () => import("../pages/ManageAddUsers/ManageAddUsers")
  );
  const ManageAddRoles = React.lazy(
    () => import("../pages/ManageAddRoles/ManageAddRoles")
  );
  const ManageTruckStopOwners = React.lazy(
    () => import("../pages/ManageTruckStopOwners/ManageTruckStopOwners")
  );
  const ManageTruckStopOwnersDetail = React.lazy(
    () => import("../pages/ManageTruckStopOwnerDetail/TruckStopOwnerDetails")
  )
  const ManageTransportCompany = React.lazy(
    () => import("../pages/ManageTransportCompany/ManageTransportCompany")
  );
  const ManageTierUpgradeRequest = React.lazy(
    () => import("../pages/ManageTierUpgradeRequest/ManageTierUpgradeRequest")
  );
  const ManageTierUpgradeRequestDetail = React.lazy(
    () => import("../pages/TierUpgradeDetail/TierUpgradeDetail")
  );
  const ManageNotifications = React.lazy(
    () => import("../pages/ManageNotifications/ManageNotifications")
  );
  const AddNotifications = React.lazy(
    () => import("../pages/AddNotifications/AddNotifications")
  );
  const ViewNotifications = React.lazy(
    () => import("../pages/ViewNotifications/ViewNotifications")
  );
  const ManageCMS = React.lazy(
    () => import("../pages/ManageCMS/ManageCMS")
  );
  const AddCMS = React.lazy(
    () => import("../pages/AddCMS/AddCMS")
  );
  const ManageBanners = React.lazy(
    () => import("../pages/ManageBanners/ManageBanners")
  );
  const CreateBanners = React.lazy(
    () => import("../pages/CreateBanners/CreateBanners")
  );
  const ManageCampaigns = React.lazy(
    () => import("../pages/ManageCampaigns/ManageCampaigns")
  )
  const CreateCampaigns = React.lazy(
    () => import("../pages/CreateCampaigns/CreateCampaigns")
  );
  const ManageAmenities = React.lazy(
    () => import("../pages/ManageAmenities/ManageAmenities")
  );
  const ManageFoodFranchise = React.lazy(
    () => import("../pages/ManageFoodFranchise/ManageFoodFranchise")
  )
  const ManageTraining = React.lazy(
    () => import("../pages/ManageTraining/ManageTraining")
  );
  const ManageTrainingChapters = React.lazy(
    () => import("../pages/ManageTrainingChapters/ManageTrainingChapters")
  )
  const ManageAddTrainingChapters = React.lazy(
    () => import("../pages/AddTrainingChapters/AddTrainingChapters")
  )
  const ManageCountry = React.lazy(
    () => import("../pages/ManageCountry/ManageCountry")
  );
  const ManageProvince = React.lazy(
    () => import("../pages/ManageProvince/ManageProvince")
  );
  const ManageCity = React.lazy(
    () => import("../pages/ManageCity/ManageCity")
  );
  const ManageDriver = React.lazy(
    () => import("../pages/ManageDrivers/ManageDrivers")
  )
  const ManageDriverDetail = React.lazy(
    () => import("../pages/ManageDriverDetail/ManageDriverDetail")
  )
  const ManageDriverLevel = React.lazy(
    () => import("../pages/ManageDriverLevel/ManageDriverLevel")
  )
  const ManageDriverLevelDetail = React.lazy(
    () => import("../pages/CreateDriverLevel/CreateDriverLevel")
  )
  const ManageSubscription = React.lazy(
    () => import("../pages/ManageSubscription/ManageSubscription")
  )
  const CreateSubscription = React.lazy(
    () => import("../pages/CreateSubscription/CreateSubscription")
  )
  const ManageShopCategory = React.lazy(
    () => import("../pages/ManageShopCategory/ManageShopCategory")
  )
  const ManageRewardCategory = React.lazy(
    () => import("../pages/ManageRewardCategory/ManageRewardCategory")
  )
  const ManageSiteCategory = React.lazy(
    () => import("../pages/ManageSiteCategory/ManageSiteCategory")
  )
  const ManageReportedIssueType = React.lazy(
    () => import("../pages/ManageReportedIssueType/ManageReportedIssueType")
  )
  const ManageRewards = React.lazy(
    () => import("../pages/ManageRewards/ManageRewards")
  )
  const ManageReport = React.lazy(
    () => import("../pages/ManageReport/ManageReport")
  )
  const ManageCreateRewards = React.lazy(
    () => import("../pages/ManageCreateRewards/ManageCreateRewards")
  )
  const ManageViewRewards = React.lazy(
    () => import("../pages/ManageViewRewards/ManageViewRewards")
  )
  const ManageReportedIssues = React.lazy(
    () => import("../pages/ManageReportedIssues/ManageReportedIssues")
  );
  const ManageReportedIssuesDetail = React.lazy(
    () => import("../pages/ManageReportedIssueDetail/ManageReportedIssueDetail")
  );
  const ReviewFeedback = React.lazy(
    () => import("../pages/ManageReviewFeedback/ManageReviewFeedback")
  );
  const ReviewFeedbackView = React.lazy(
    () => import("../pages/ManageFeedbackView/ManageFeedbackView")
  );
  const ReviewForm = React.lazy(
    () => import("../pages/ManageReviewForm/ManageReviewForm")
  );
  const ManageReferrals = React.lazy(
    () => import("../pages/ManageReferrals/ManageReferrals")
  );
  const ManageOnboardingSlides = React.lazy(
    () => import("../pages/ManageOnboardingSlides/ManageOnboardingSlides")
  );
  const Settings = React.lazy(
    () => import("../pages/Settings/Settings")
  );
  const AppSetting = React.lazy(
    () => import("../pages/AppSetting/AppSetting")
  );
  const PointSetting = React.lazy(
    () => import("../pages/PointSetting/PointSetting")
  );
  const RewardSetting = React.lazy(
    () => import("../pages/RewardSetting/RewardSetting")
  );
  const SocialMediaLinks = React.lazy(
    () => import("../pages/SocialMediaLinks/SocialMediaLinks")
  );
  const ForceUpdate = React.lazy(
    () => import("../pages/ForceUpdate/ForceUpdate")
  );
  const AppSupport = React.lazy(
    () => import("../pages/AppSupport/AppSupport")
  );
  const RequestCallback = React.lazy(
    () => import("../pages/RequestCallback/RequestCallback")
  );
  const SettingsHub = React.lazy(
    () => import("../pages/ManageHubSettings/ManageHubSettings")
  );
  const FlaggedAccounts = React.lazy(
    () => import("../pages/FlaggedAccounts/FlaggedAccounts")
  );
  const AuditLogs = React.lazy(
    () => import("../pages/AuditLogs/AuditLogs")
  );


  return (
    <>
      <div id="main-wraper">
        <Routes>
          {/* <Route
            path="/login"
            element={
              <Suspense fallback={<></>}>
                <Login />
              </Suspense>
            }
          /> */}
          {/* <Route
            path="/forgot-password"
            element={
              <Suspense fallback={<></>}>
                <ForgotPassword />
              </Suspense>
            }
          />
          <Route
            path="/verify-otp"
            element={
              <Suspense fallback={<></>}>
                <VerifyOTP />
              </Suspense>
            }
          />
          <Route
            path="/create-password"
            element={
              <Suspense fallback={<></>}>
                <CreatePassword />
              </Suspense>
            }
          /> */}
          <Route
            path="/login"
            element={
              <Suspense fallback={<></>}>
                <PublicRoute element={<Login />} />
              </Suspense>
            }
          />
          <Route
            path="/forgot-password"
            element={
              <Suspense fallback={<></>}>
                <PublicRoute element={<ForgotPassword />} />
              </Suspense>
            }
          />
          <Route
            path="/verify-otp"
            element={
              <Suspense fallback={<></>}>
                <PublicRoute element={<VerifyOTP />} />
              </Suspense>
            }
          />
          <Route
            path="/create-password"
            element={
              <Suspense fallback={<></>}>
                <PublicRoute element={<CreatePassword />} />
              </Suspense>
            }
          />

          <Route
            path="/"
            element={
              <ProtectedRoute
                {...defaultProtectedRouteProps}
                outlet={<Main />}
              />
            }
          >
            {/* Add this default redirect */}
            {/* <Route index element={<Navigate to="/dashboard" replace />} /> */}
            <Route
              path="/my-profile"
              element={
                <Suspense fallback={<></>}>
                  <MyProfile />
                </Suspense>
              }
            />
            <Route
              path="/dashboard"
              element={
                <Suspense fallback={<></>}>
                  <Dashboard />
                </Suspense>
              }
            />
            <Route
              path="/business-revenue"
              element={
                <Suspense fallback={<></>}>
                  <BusinessRevenue />
                </Suspense>
              }
            />
            <Route
              path="/roles"
              element={
                <Suspense fallback={<></>}>
                  <ManageRoles />
                </Suspense>
              }
            />
            <Route
              path="/admin-users"
              element={
                <Suspense fallback={<></>}>
                  <ManageUsers />
                </Suspense>
              }
            />
            <Route
              path="/manage-add-users"
              element={
                <Suspense fallback={<></>}>
                  <ManageAddUsers />
                </Suspense>
              }
            />
            <Route
              path="/manage-add-roles"
              element={
                <Suspense fallback={<></>}>
                  <ManageAddRoles />
                </Suspense>
              }
            />
            <Route
              path="/truck-stop-owner"
              element={
                <Suspense fallback={<></>}>
                  <ManageTruckStopOwners />
                </Suspense>
              }
            />
            <Route
              path="/truck-stop-owner-detail"
              element={
                <Suspense fallback={<></>}>
                  <ManageTruckStopOwnersDetail />
                </Suspense>
              }
            />
            <Route
              path="/transport-company"
              element={
                <Suspense fallback={<></>}>
                  <ManageTransportCompany />
                </Suspense>
              }
            />
            <Route
              path="/tier-upgrade-request"
              element={
                <Suspense fallback={<></>}>
                  <ManageTierUpgradeRequest />
                </Suspense>
              }
            />
            <Route
              path="/tier-upgrade-detail"
              element={
                <Suspense fallback={<></>}>
                  <ManageTierUpgradeRequestDetail />
                </Suspense>
              }
            />
            <Route
              path="/notifications"
              element={
                <Suspense fallback={<></>}>
                  <ManageNotifications />
                </Suspense>
              }
            />
            <Route
              path="/add-notifications"
              element={
                <Suspense fallback={<></>}>
                  <AddNotifications />
                </Suspense>
              }
            />
            <Route
              path="/view-notifications"
              element={
                <Suspense fallback={<></>}>
                  <ViewNotifications />
                </Suspense>
              }
            />
            <Route
              path="/cms"
              element={
                <Suspense fallback={<></>}>
                  <ManageCMS />
                </Suspense>
              }
            />
            <Route
              path="/add-cms"
              element={
                <Suspense fallback={<></>}>
                  <AddCMS />
                </Suspense>
              }
            />
            <Route
              path="/banner"
              element={
                <Suspense fallback={<></>}>
                  <ManageBanners />
                </Suspense>
              }
            />
            <Route
              path="/create-banners"
              element={
                <Suspense fallback={<></>}>
                  <CreateBanners />
                </Suspense>
              }
            />
            <Route
              path="/campaign"
              element={
                <Suspense fallback={<></>}>
                  <ManageCampaigns />
                </Suspense>
              }
            />
            <Route
              path="/create-campaigns"
              element={
                <Suspense fallback={<></>}>
                  <CreateCampaigns />
                </Suspense>
              }
            />
            <Route
              path="/amenities"
              element={
                <Suspense fallback={<></>}>
                  <ManageAmenities />
                </Suspense>
              }
            />
            <Route
              path="/food-franchise"
              element={
                <Suspense fallback={<></>}>
                  <ManageFoodFranchise />
                </Suspense>
              }
            />
            <Route
              path="/training"
              element={
                <Suspense fallback={<></>}>
                  <ManageTraining />
                </Suspense>
              }
            />
            <Route
              path="/training-chapters"
              element={
                <Suspense fallback={<></>}>
                  <ManageTrainingChapters />
                </Suspense>
              }
            />
            <Route
              path="/create-training-chapters"
              element={
                <Suspense fallback={<></>}>
                  <ManageAddTrainingChapters />
                </Suspense>
              }
            />
            <Route
              path="/country"
              element={
                <Suspense fallback={<></>}>
                  <ManageCountry />
                </Suspense>
              }
            />
            <Route
              path="/manage-province"
              element={
                <Suspense fallback={<></>}>
                  <ManageProvince />
                </Suspense>
              }
            />
            <Route
              path="/manage-city"
              element={
                <Suspense fallback={<></>}>
                  <ManageCity />
                </Suspense>
              }
            />
            <Route
              path="/driver"
              element={
                <Suspense fallback={<></>}>
                  <ManageDriver />
                </Suspense>
              }
            />
            <Route
              path="/driver-detail"
              element={
                <Suspense fallback={<></>}>
                  <ManageDriverDetail />
                </Suspense>
              }
            />
            <Route
              path="/driver-level"
              element={
                <Suspense fallback={<></>}>
                  <ManageDriverLevel />
                </Suspense>
              }
            />
            <Route
              path="/create-driver-level"
              element={
                <Suspense fallback={<></>}>
                  <ManageDriverLevelDetail />
                </Suspense>
              }
            />
            <Route
              path="/subscription"
              element={
                <Suspense fallback={<></>}>
                  <ManageSubscription />
                </Suspense>
              }
            />
            <Route
              path="/create-subscription"
              element={
                <Suspense fallback={<></>}>
                  <CreateSubscription />
                </Suspense>
              }
            />
            <Route
              path="/shop-category"
              element={
                <Suspense fallback={<></>}>
                  <ManageShopCategory />
                </Suspense>
              }
            />
            <Route
              path="/reward-category"
              element={
                <Suspense fallback={<></>}>
                  <ManageRewardCategory />
                </Suspense>
              }
            />
            <Route
              path="/site-category"
              element={
                <Suspense fallback={<></>}>
                  <ManageSiteCategory />
                </Suspense>
              }
            />
            <Route
              path="/reported-issue-types"
              element={
                <Suspense fallback={<></>}>
                  <ManageReportedIssueType />
                </Suspense>
              }
            />
            <Route
              path="/reward"
              element={
                <Suspense fallback={<></>}>
                  <ManageRewards />
                </Suspense>
              }
            />
            <Route
              path="/reports"
              element={
                <Suspense fallback={<></>}>
                  <ManageReport />
                </Suspense>
              }
            />
            <Route
              path="/create-rewards"
              element={
                <Suspense fallback={<></>}>
                  <ManageCreateRewards />
                </Suspense>
              }
            />
            <Route
              path="/view-reward"
              element={
                <Suspense fallback={<></>}>
                  <ManageViewRewards />
                </Suspense>
              }
            />
            <Route
              path="/reported-alerts"
              element={
                <Suspense fallback={<></>}>
                  <ManageReportedIssues />
                </Suspense>
              }
            />
            <Route
              path="/reported-issues-detail"
              element={
                <Suspense fallback={<></>}>
                  <ManageReportedIssuesDetail />
                </Suspense>
              }
            />
            <Route
              path="/review-feedback"
              element={
                <Suspense fallback={<></>}>
                  <ReviewFeedback />
                </Suspense>
              }
            />
            <Route
              path="/reviews-feedback-view"
              element={
                <Suspense fallback={<></>}>
                  <ReviewFeedbackView />
                </Suspense>
              }
            />
            <Route
              path="/review-form"
              element={
                <Suspense fallback={<></>}>
                  <ReviewForm />
                </Suspense>
              }
            />
            <Route
              path="/referrals"
              element={
                <Suspense fallback={<></>}>
                  <ManageReferrals />
                </Suspense>
              }
            />
            <Route
              path="/onboarding-slides"
              element={
                <Suspense fallback={<></>}>
                  <ManageOnboardingSlides />
                </Suspense>
              }
            />
            <Route
              path="/settings"
              element={
                <Suspense fallback={<></>}>
                  <Settings />
                </Suspense>
              }
            />
            <Route
              path="/app-setting"
              element={
                <Suspense fallback={<></>}>
                  <AppSetting />
                </Suspense>
              }
            />
            <Route
              path="/point-setting"
              element={
                <Suspense fallback={<></>}>
                  <PointSetting />
                </Suspense>
              }
            />
            <Route
              path="/reward-referral-setting"
              element={
                <Suspense fallback={<></>}>
                  <RewardSetting />
                </Suspense>
              }
            />
            <Route
              path="/social-media-links"
              element={
                <Suspense fallback={<></>}>
                  <SocialMediaLinks />
                </Suspense>
              }
            />
            <Route
              path="/app-version"
              element={
                <Suspense fallback={<></>}>
                  <ForceUpdate />
                </Suspense>
              }
            />
            <Route
              path="/app-support"
              element={
                <Suspense fallback={<></>}>
                  <AppSupport />
                </Suspense>
              }
            />
            <Route
              path="/request-callback"
              element={
                <Suspense fallback={<></>}>
                  <RequestCallback />
                </Suspense>
              }
            />
            <Route
              path="/integration-hub-settings"
              element={
                <Suspense fallback={<></>}>
                  <SettingsHub />
                </Suspense>
              }
            />

            <Route
              path="/flagged-accounts"
              element={
                <Suspense fallback={<></>}>
                  <FlaggedAccounts />
                </Suspense>
              }
            />

             <Route
              path="/audit-log"
              element={
                <Suspense fallback={<></>}>
                  <AuditLogs />
                </Suspense>
              }
            />

          </Route>
        </Routes>
      </div>
    </>
  );
};

export default Navigation;
